import * as React from 'react';
import styled from 'styled-components';
import {TscStyles, TscThemeName, TscThemeNames} from '@techsmith/tsc-cloud-style-guide';
import {useTranslation} from 'react-i18next';
import {ICollectionModel} from '../../../model/appModel';
import {IMediaAnalyticsModel} from '../../../model/mediaAnalyticsModel';
import {useTscTheme} from '../../../context/themeProvider';

const UserBehaviorWrapper = styled.div`
   display: flex;
   justify-content: space-between;
   min-height: 5rem;
   overflow-x: auto;
`;

const DoubleWideColumnWrapper = styled.td`
   flex: 2 1 50%;
   margin-right: .75rem;
   white-space: nowrap;
   overflow: hidden;
   text-overflow: ellipsis;
`;

const SingleWideColumnWrapper = styled.td`
   flex: 1 0 25%;
   margin-right: .75rem;
`;

const ColumnLabel = styled.div<{theme: TscThemeNames}>`
   font-weight: 600;
   color: ${prop => prop.theme === TscThemeName.dusk ? TscStyles.color.text.lightMedium : TscStyles.color.text.mediumDark};
   font-size: 0.875rem;
`;

export const ViewerWrapper = styled.tr`
   margin-top: .75rem;
   list-style-type: none;
   display: flex;
`;

const EmptyData = styled.div`
   display: flex;
   flex: 1 0 auto;
   font-style: italic;
   justify-content: center;
   align-items: center;
`;

const ColumnLabelWrapper = styled.th`
   text-align: start;
   display: flex;
`;

const UserAnalyticsList = styled.div`
   display: flex;
`;

const DataTableWrapper = styled.div`
   display: flex;
   flex: 1 0 auto;
   flex-direction: column;
`;

const UserDataTable = styled.table`
   display: flex;
   flex: 1 0 auto;
   flex-direction: column;
   padding: 0;
   margin: 0;
`;

export const UserBehaviorDataBase: React.FC<IUserBehaviorDataProps> = ({analytics, selectedCollection}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const analyticsIds: string[] = analytics.map(a => a.TechSmithId);

   const getViewerPercentWatched = (viewer: IMediaAnalyticsModel): number => Math.round(viewer.PercentViewed * 1000) / 10;

   const getViewerStatus = (viewer: IMediaAnalyticsModel): string => {
      const percentWatched = getViewerPercentWatched(viewer);

      if (percentWatched === 0) {
         return t('analytics.viewerStatus.notStarted');
      } else if (percentWatched === 100) {
         return t('analytics.viewerStatus.completed');
      }
      return t('analytics.viewerStatus.started');
   };

   const getSortedViewers = (viewers: IMediaAnalyticsModel[]) => viewers.sort((firstViewer: IMediaAnalyticsModel, secondViewer: IMediaAnalyticsModel) => {
      const firstViewerName = firstViewer.DisplayName ? firstViewer.DisplayName : `${firstViewer.FirstName} ${firstViewer.LastName}`;
      const secondViewerName = secondViewer.DisplayName ? secondViewer.DisplayName : `${secondViewer.FirstName} ${secondViewer.LastName}`;

      return firstViewerName.toLowerCase() < secondViewerName.toLowerCase() ? -1 : 1;
   });

   const viewerIds = selectedCollection?.MemberDetails.filter(member => analyticsIds.find(id => id === member.TechSmithId)).map(member => member.TechSmithId);
   const unsortedViewers = selectedCollection ? analytics.filter(user => viewerIds.find(id => id === user.TechSmithId)) : analytics;
   const sortedViewers = getSortedViewers(unsortedViewers);
   const emptyState = (
      <EmptyData>
         {t('analytics.emptyDataState')}
      </EmptyData>
   );
   const nonEmptyState = (
      <DataTableWrapper>
         <ColumnLabelWrapper>
            <DoubleWideColumnWrapper>
               <ColumnLabel theme={theme}>
                  {t('analytics.viewerLabel')}
               </ColumnLabel>
            </DoubleWideColumnWrapper>
            <SingleWideColumnWrapper>
               <ColumnLabel theme={theme}>
                  {t('analytics.statusLabel')}
               </ColumnLabel>
            </SingleWideColumnWrapper>
            <SingleWideColumnWrapper>
               <ColumnLabel theme={theme}>
                  {t('analytics.completionLabel')}
               </ColumnLabel>
            </SingleWideColumnWrapper>
         </ColumnLabelWrapper>
         <UserAnalyticsList>
            <UserDataTable>
               {sortedViewers.map((viewer, index) =>
                  (
                     <ViewerWrapper
                        data-test-id="viewer-wrapper"
                        key={index}
                     >
                        <DoubleWideColumnWrapper>
                           {viewer.DisplayName ? viewer.DisplayName : `${viewer.FirstName} ${viewer.LastName}`}
                        </DoubleWideColumnWrapper>
                        <SingleWideColumnWrapper>
                           {getViewerStatus(viewer)}
                        </SingleWideColumnWrapper>
                        <SingleWideColumnWrapper>
                           {t('analytics.percentage', {value: getViewerPercentWatched(viewer).toString()})}
                        </SingleWideColumnWrapper>
                     </ViewerWrapper>
                  )
               )}
            </UserDataTable>
         </UserAnalyticsList>
      </DataTableWrapper>
   );

   return (
      <UserBehaviorWrapper data-test-id="user-behavior-data">
         {!analytics.length ? emptyState : nonEmptyState}
      </UserBehaviorWrapper>
   );
};

export interface IUserBehaviorDataProps {
   analytics: IMediaAnalyticsModel[];
   selectedCollection: ICollectionModel;
}

export default React.memo(UserBehaviorDataBase);
